import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Image, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useTable } from '@/hooks/useTable';
import {
  ProductStock,
  ProductStockStatus,
  PayType,
  getProductStockList,
  updateProductStatus,
  PayTypeMap,
} from '@/api/product';
import {
  loadProductCategories,
  productCategoriesAtom,
} from '@/atoms/productCategories';
import { useAtom } from 'jotai';
import dayjs from 'dayjs';
import ProductDrawer from './ProductDrawer';
import ProductHistoryDrawer from './ProductHistoryDrawer';

const ProductsPage: React.FC = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [, setProductCategories] = useAtom(productCategoriesAtom);

  // 加载商品分类
  useEffect(() => {
    loadProductCategories(setProductCategories);
  }, [setProductCategories]);

  // 表格数据管理
  const {
    data,
    total,
    currentPage,
    pageSize,
    isLoading,
    refresh,
    reload,
    handleTableChange,
  } = useTable({
    queryKey: ['products', 'list'].join(','),
    queryFn: async (currentPage, pageSize) => {
      try {
        const res = await getProductStockList({
          currentPage,
          pageSize,
        });
        return res.data;
      } catch (error) {
        message.error('获取商品列表失败，请重试');
        return {
          list: [],
          total: 0,
        };
      }
    },
  });

  // 商品状态切换
  const handleStatusChange = async (record: ProductStock) => {
    try {
      const newStatus =
        record.productStockStatus === ProductStockStatus.OnShelf
          ? ProductStockStatus.OffShelf
          : ProductStockStatus.OnShelf;

      await updateProductStatus({
        productStockId: record.productStockId,
        productStockStatus: newStatus,
      });
      message.success('状态更新成功');
      reload(); // 使用 reload 而不是 refresh，保持当前页码
    } catch (error) {
      message.error('状态更新失败，请重试');
    }
  };

  // 打开新增商品抽屉
  const handleAdd = () => {
    setDrawerVisible(true);
  };

  // 编辑商品
  const handleEdit = (record: ProductStock) => {
    setEditingProduct(record);
    setDrawerVisible(true);
  };

  const [editingProduct, setEditingProduct] = useState<ProductStock | null>(
    null
  );

  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number>();

  // 查看历史记录
  const handleViewHistory = (record: ProductStock) => {
    setSelectedProductId(record.productStockId);
    setHistoryDrawerVisible(true);
  };

  // 关闭历史记录抽屉
  const handleHistoryDrawerClose = () => {
    setHistoryDrawerVisible(false);
    setSelectedProductId(undefined);
  };

  // 关闭抽屉并刷新数据
  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingProduct(null);
  };

  const handleDrawerSubmitClose = () => {
    handleDrawerClose()
    reload();
  }
  // 表格列配置
  const columns: ColumnsType<ProductStock> = [
    {
      title: '展示图',
      dataIndex: 'productShowImages',
      width: 100,
      render: (images: string) => {
        try {
          const imageList = JSON.parse(images);
          return imageList?.[0]?.imageUrl ? (
            <Image
              src={imageList[0].imageUrl}
              alt="商品展示图"
              width={80}
              height={80}
              style={{ objectFit: 'cover' }}
            />
          ) : null;
        } catch (error) {
          return null;
        }
      },
    },
    {
      title: '商品标题',
      dataIndex: 'productTitle',
      key: 'productTitle',
      width: 200,
    },
    {
      title: '库存编码',
      dataIndex: 'stockCode',
      key: 'stockCode',
      width: 120,
    },
    {
      title: '分类',
      dataIndex: 'categoryTitle',
      width: 120,
    },
    {
      title: '支付类型',
      dataIndex: 'payType',
      width: 100,
      render: (payType: PayType) => (
        <Tag>{PayTypeMap.get(payType)}</Tag>
      ),
    },
    {
      title: '价格',
      dataIndex: 'price',
      width: 100,
      render: (price: number) => price?.toFixed(2),
    },
    {
      title: '库存',
      dataIndex: 'stock',
      width: 100,
    },
    {
      title: '状态',
      dataIndex: 'productStockStatus',
      width: 100,
      render: (status: ProductStockStatus) => (
        <Tag
          color={status === ProductStockStatus.OnShelf ? 'success' : 'default'}
        >
          {status === ProductStockStatus.OnShelf ? '已上架' : '已下架'}
        </Tag>
      ),
    },
    {
      title: '创建人',
      dataIndex: 'createUser',
      width: 120,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 160,
      render: (time: number) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" onClick={() => handleStatusChange(record)}>
            {record.productStockStatus === ProductStockStatus.OnShelf
              ? '下架'
              : '上架'}
          </Button>
          <Button type="link" onClick={() => handleViewHistory(record)}>
            历史记录
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="商品管理"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增商品
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={data}
        rowKey="productStockId"
        loading={isLoading}
        scroll={{ x: 1500 }}
        pagination={{
          total: total,
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        onChange={handleTableChange}
      />

      {/* 商品抽屉表单 */}
      {
        drawerVisible && (
          <ProductDrawer
            onSuccess={handleDrawerSubmitClose}
            onClose={handleDrawerClose}
            open={drawerVisible}
            editingProduct={editingProduct}
          />
        )
      }
      {/* 商品历史记录抽屉 */}
      <ProductHistoryDrawer
        open={historyDrawerVisible}
        onClose={handleHistoryDrawerClose}
        productStockId={selectedProductId}
      />
    </Card>
  );
};

export default ProductsPage;
